// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import { useCallback } from 'react';
import type { SettingsKey, SettingsValues } from '../types.js';
import { useApp } from '../store/AppContext.js';
import { buildEvent } from '../lib/eventLog.js';
import { useEncryptAndPush } from './useEncryptAndPush.js';

export function useSettings(worker: Worker | null) {
  const { state } = useApp();
  const encryptAndPush = useEncryptAndPush(worker, state.auth.keyData);

  const updateSetting = useCallback(
    async <K extends SettingsKey>(key: K, value: SettingsValues[K]): Promise<void> => {
      const event = await buildEvent(
        'SettingUpdated',
        { key, value: value as SettingsValues[SettingsKey] },
        state.ledger.lastSequence + 1,
      );
      await encryptAndPush(event);
    },
    [encryptAndPush, state.ledger.lastSequence],
  );

  const getSetting = useCallback(
    <K extends SettingsKey>(key: K): SettingsValues[K] => state.appSettings[key],
    [state.appSettings],
  );

  return {
    settings: state.appSettings,
    updateSetting,
    getSetting,
    homeCurrency: state.appSettings.homeCurrency,
    dateFormat: state.appSettings.dateFormat,
    costBasisMethod: state.appSettings.costBasisMethod,
    coinbaseCredential: state.appSettings.coinbaseCredential,
    isCoinbaseConnected: state.appSettings.coinbaseCredential !== null,
  };
}
